/** Fetch the Matrix surface protocol. Same origin when the Worker serves ui/dist. */

import type {
  ChangeDetail,
  ReplayDetail,
  StateDelta,
  StateFiles,
  SurfaceSnapshot,
} from '../generated/surface.js';
import type { SurfaceConnectionState } from './surface-connection.js';
import { isChangeDetail, isStateDelta, isStateFiles, isSurfaceSnapshot } from './surface-validate.js';
import type { WorkObservation } from './lenses/types.js';
import { parseWorkObservation } from './lenses/work-observation.js';

/**
 * Memory-only bearer client for one served surface origin, reading one
 * project at a time. Every `/v1/` request it makes, the snapshot stream
 * included, names that project as `?project=`; the site Worker reads nothing
 * for a project the account is not a member of.
 */
export type SurfaceClient = {
  /** The project every later read names; null names none, and the origin chooses. */
  selectProject: (project: string | null) => void;
  project: () => string | null;
  connect: (bearer: string, externalSignal?: AbortSignal) => Promise<void>;
  fetchSnapshot: (externalSignal?: AbortSignal) => Promise<SurfaceSnapshot>;
  fetchActiveWork: (project: string, externalSignal?: AbortSignal) => Promise<WorkObservation>;
  fetchChangeDetail: (id: string, externalSignal?: AbortSignal) => Promise<ChangeDetail>;
  fetchReplayDetail: (digest: string, externalSignal?: AbortSignal) => Promise<ReplayDetail>;
  fetchStateFiles: (root: string, externalSignal?: AbortSignal) => Promise<StateFiles>;
  fetchStateFile: (root: string, path: string, externalSignal?: AbortSignal) => Promise<StateFile>;
  fetchStateDelta: (
    base: string,
    result: string,
    externalSignal?: AbortSignal,
  ) => Promise<StateDelta>;
  subscribeSnapshots: (
    onSnapshot: (snapshot: SurfaceSnapshot) => void,
    onConnectionState?: (state: SurfaceConnectionState) => void,
    onClosed?: () => void,
  ) => () => void;
};

/** One file's exact bytes from a certified state, with the digest the authority stated. */
export type StateFile = { bytes: Uint8Array; digest: string | null };

type CredentialState = {
  bearer: string | undefined;
  /** The project each request names. */
  project: string | null;
};

/**
 * Why a surface read was not authorized, which decides who has to act.
 *
 * `noSession` is the browser's own problem and signing in resolves it.
 * `refused` is not: the website Worker holds the authority credential and
 * attaches it server-side, so a 401 that is not `no_session` is the authority
 * turning down the *site's* bearer while this browser's session is perfectly
 * good. Collapsing the two sent a freshly signed-in person back to the
 * sign-in form to retype credentials that had already been accepted.
 * `noProject` is a session that is not a member of the project asked for:
 * signing in again changes nothing, and the person's own projects still read.
 */
type AuthenticationReason = 'missingCredential' | 'noSession' | 'noProject' | 'refused';

const STATE_URL = '/v1/surface/state';
const EVENTS_URL = '/v1/surface/events';
const SESSION_URL = '/v1/surface/session';
const CHANGE_DETAIL_PREFIX = '/v1/surface/changes';
const STATE_FILES_PREFIX = '/v1/surface/states';
const REPLAY_DETAIL_PREFIX = '/v1/surface/replays';
const REPLAY_SCHEMA = 'algodyne.matrix.replay-projection/v1';
const HISTORICAL_REPLAY_SCHEMA = 'algodyne.matrix.historical-replay/v1';
const REPLAY_DIGEST = /^[a-f0-9]{64}$/;
// One file-backed snapshot can take several seconds. The origin now
// single-flights GET /state with SSE, but a cold compute still has to finish.
const STATE_TIMEOUT_MS = 30_000;

/**
 * 404 on a drill-down route means the entity is not in this authority.
 * That is not an empty document: a present change with no mutations is
 * 200 and `mutations: []`. Fabricating a blank ChangeDetail would hide
 * the miss from the operator.
 */
export class SurfaceNotFoundError extends Error {
  readonly kind: 'change' | 'state' | 'delta' | 'replay' | 'file';
  readonly identity: string;

  constructor(kind: 'change' | 'state' | 'delta' | 'replay' | 'file', identity: string) {
    super(`${kind} ${identity} is not in this authority`);
    this.name = 'SurfaceNotFoundError';
    this.kind = kind;
    this.identity = identity;
  }
}

/**
 * A drill-down the authority answered with a non-OK status other than 404 or
 * an authentication refusal. `reason` is the authority's own text, so a 413
 * names the file size and the cap rather than a bare status.
 */
export class SurfaceRefusalError extends Error {
  readonly status: number;
  readonly reason: string;

  constructor(label: string, status: number, reason: string) {
    super(`surface ${label} ${status}: ${reason}`);
    this.name = 'SurfaceRefusalError';
    this.status = status;
    this.reason = reason;
  }
}

/** Typed refusal for a missing or rejected surface credential. */
export class SurfaceAuthenticationError extends Error {
  readonly reason: AuthenticationReason;
  readonly status: number | undefined;

  constructor(reason: AuthenticationReason, status?: number) {
    super(authenticationMessage(reason, status));
    this.name = 'SurfaceAuthenticationError';
    this.reason = reason;
    this.status = status;
  }
}

function authenticationMessage(
  reason: AuthenticationReason,
  status: number | undefined,
): string {
  switch (reason) {
    case 'missingCredential':
      return 'surface credential is required';
    case 'noSession':
      return 'this browser holds no Matrix session';
    case 'noProject':
      return 'this account is not a member of that project';
    case 'refused': {
      const statusText = status?.toString();
      return statusText ? `surface credential refused (${statusText})` : 'surface credential refused';
    }
    default: {
      const exhausted: never = reason;
      return exhausted;
    }
  }
}

async function connect(
  credential: CredentialState,
  bearer: string,
  externalSignal?: AbortSignal,
): Promise<void> {
  const normalized = bearer.trim();
  if (normalized.length === 0) {
    throw new SurfaceAuthenticationError('missingCredential');
  }
  credential.bearer = undefined;
  const response = await fetch(projectScopedUrl(SESSION_URL, credential.project), {
    method: 'POST',
    headers: { authorization: `Bearer ${normalized}` },
    signal: requestSignal(externalSignal),
  });
  await rejectAuthenticationFailure(response);
  if (!response.ok) {
    throw new Error(`surface session ${response.status}`);
  }
  credential.bearer = normalized;
}

/**
 * What a gateway says when it could not reach the authority at all.
 *
 * The site Worker answers 502 or 504 with a reason rather than passing an
 * authority response through, because there was none to pass. Carrying the
 * distinction here is what stops the UI reporting a refusal by a service that
 * was never asked.
 */
export const GATEWAY_REASON: ReadonlyMap<string, string> = new Map([
  ['authority_unreachable', 'The authority could not be reached. Check that it is serving, then retry.'],
  ['authority_timeout', 'The authority did not answer in time. It may be busy; retry the connection.'],
]);

async function gatewayFailure(response: Response): Promise<string | null> {
  if (response.status !== 502 && response.status !== 504) return null;
  const body = await response.clone().text();
  let reason = '';
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed && typeof parsed === 'object' && 'reason' in parsed) {
      reason = String((parsed as { reason: unknown }).reason);
    }
  } catch {
    return null;
  }
  return GATEWAY_REASON.get(reason) ?? null;
}

async function fetchSnapshot(
  credential: CredentialState,
  externalSignal?: AbortSignal,
): Promise<SurfaceSnapshot> {
  const response = await fetch(projectScopedUrl(STATE_URL, credential.project), {
    headers: authorizationHeaders(credential),
    signal: requestSignal(externalSignal),
  });
  await rejectAuthenticationFailure(response);
  if (!response.ok) {
    const gateway = await gatewayFailure(response);
    throw new Error(gateway ?? `surface state ${response.status}`);
  }
  return parseSnapshot(await response.text());
}

async function fetchActiveWork(
  credential: CredentialState,
  project: string,
  externalSignal?: AbortSignal,
): Promise<WorkObservation> {
  const headers = new Headers(authorizationHeaders(credential));
  headers.set('content-type', 'application/json');
  const response = await fetch(projectScopedUrl('/v1/authority/work/list', credential.project), {
    method: 'POST',
    headers,
    signal: requestSignal(externalSignal),
    body: JSON.stringify({ project, include_terminal: false, max_items: 256 }),
  });
  await rejectAuthenticationFailure(response);
  if (!response.ok) throw new Error(`Work observation unavailable (${response.status})`);
  const observation = parseWorkObservation(await response.json());
  if (observation.project !== project) throw new Error('Work observation names a different project');
  return observation;
}

async function fetchChangeDetail(
  credential: CredentialState,
  id: string,
  externalSignal?: AbortSignal,
): Promise<ChangeDetail> {
  return fetchDrillDown({
    credential,
    url: `${CHANGE_DETAIL_PREFIX}/${encodeURIComponent(id)}`,
    label: 'change',
    missing: { kind: 'change', identity: id },
    parse: parseChangeDetail,
    externalSignal,
  });
}

async function fetchReplayDetail(
  credential: CredentialState,
  digest: string,
  externalSignal?: AbortSignal,
): Promise<ReplayDetail> {
  if (!REPLAY_DIGEST.test(digest)) throw new Error('Replay URL needs a canonical 64-character digest.');
  return fetchDrillDown({
    credential,
    url: `${REPLAY_DETAIL_PREFIX}/${digest}`,
    label: 'replay',
    missing: { kind: 'replay', identity: digest },
    parse: (raw) => parseDocument(raw, isReplayDetail, 'replay'),
    externalSignal,
  });
}

async function fetchStateFiles(
  credential: CredentialState,
  root: string,
  externalSignal?: AbortSignal,
): Promise<StateFiles> {
  return fetchDrillDown({
    credential,
    url: `${STATE_FILES_PREFIX}/${encodeURIComponent(root)}`,
    label: 'states',
    missing: { kind: 'state', identity: root },
    parse: parseStateFiles,
    externalSignal,
  });
}

async function fetchStateFile(
  credential: CredentialState,
  root: string,
  path: string,
  externalSignal?: AbortSignal,
): Promise<StateFile> {
  const response = await drillDownResponse({
    credential,
    url: `${STATE_FILES_PREFIX}/${encodeURIComponent(root)}/file?path=${encodeURIComponent(path)}`,
    label: 'file',
    missing: { kind: 'file', identity: `${root}:${path}` },
    externalSignal,
  });
  return {
    bytes: new Uint8Array(await response.arrayBuffer()),
    digest: response.headers.get('x-matrix-content-digest'),
  };
}

async function fetchStateDelta(
  credential: CredentialState,
  base: string,
  result: string,
  externalSignal?: AbortSignal,
): Promise<StateDelta> {
  return fetchDrillDown({
    credential,
    url: `${STATE_FILES_PREFIX}/${encodeURIComponent(base)}/delta/${encodeURIComponent(result)}`,
    label: 'delta',
    missing: { kind: 'delta', identity: `${base}..${result}` },
    parse: parseStateDelta,
    externalSignal,
  });
}

/**
 * Subscribe to snapshot SSE. Returns an unsubscribe function.
 *
 * No bearer is passed and none is wanted: `EventSource` has no header to put
 * one in. The credential is the browser's session cookie, which the origin
 * exchanges for the fabric's stream credential server-side, so the stream is
 * authorized by the same thing every other read on this origin is authorized
 * by. `withCredentials` states that dependency rather than leaving it to the
 * accident of the shell and the surface sharing an origin.
 */
function subscribeSnapshots(
  credential: CredentialState,
  onSnapshot: (snapshot: SurfaceSnapshot) => void,
  onConnectionState: (state: SurfaceConnectionState) => void = () => {},
  onClosed: () => void = () => {},
): () => void {
  const source = new EventSource(projectScopedUrl(EVENTS_URL, credential.project), { withCredentials: true });
  // EventSource `open` only means the socket is up. Freshness requires a
  // parsed state frame — otherwise the chrome claims live while the body is
  // still Connecting or a timed-out GET /state.
  const errorHandler = () => {
    onConnectionState('reconnecting');
    // A non-200 reconnect (expired session, gateway 5xx) fails the source
    // permanently; the browser will never retry it, so the owner must.
    // ubs:ignore -- compares readyState with the EventSource.CLOSED constant; `source` is tainted only by its `withCredentials` option and carries no secret.
    if (source.readyState === EventSource.CLOSED) onClosed();
  };
  // The authority could not fold a snapshot: what is on screen is no longer
  // current, so the chrome must stop claiming live.
  const refusedHandler = () => onConnectionState('reconnecting');
  const handler = (event: MessageEvent<string>) => {
    try {
      const snapshot = parseSnapshot(event.data);
      onConnectionState('live');
      onSnapshot(snapshot);
    } catch {
      // A malformed frame is not a fresh one.
      onConnectionState('reconnecting');
    }
  };
  source.addEventListener('error', errorHandler);
  source.addEventListener('refused', refusedHandler);
  source.addEventListener('state', handler);
  return () => {
    source.removeEventListener('error', errorHandler);
    source.removeEventListener('refused', refusedHandler);
    source.removeEventListener('state', handler);
    source.close();
  };
}

/**
 * `url` naming `project` as `?project=`, the one parameter the site Worker
 * selects a membership by; null leaves the choice to the origin. Every `/v1/`
 * request goes through this, so no read can reach a project other than the
 * one the client was told to read.
 */
export function projectScopedUrl(url: string, project: string | null): string {
  if (project === null) return url;
  return `${url}${url.includes('?') ? '&' : '?'}project=${encodeURIComponent(project)}`;
}

function requestSignal(externalSignal?: AbortSignal): AbortSignal {
  const timeoutSignal = AbortSignal.timeout(STATE_TIMEOUT_MS);
  return externalSignal ? AbortSignal.any([externalSignal, timeoutSignal]) : timeoutSignal;
}

type DrillDown = {
  credential: CredentialState;
  url: string;
  label: string;
  missing: { kind: SurfaceNotFoundError['kind']; identity: string };
  externalSignal?: AbortSignal;
};

async function fetchDrillDown<T>(options: DrillDown & { parse: (raw: string) => T }): Promise<T> {
  const response = await drillDownResponse(options);
  return options.parse(await response.text());
}

async function drillDownResponse(options: DrillDown): Promise<Response> {
  const response = await fetch(projectScopedUrl(options.url, options.credential.project), {
    headers: authorizationHeaders(options.credential),
    signal: requestSignal(options.externalSignal),
  });
  await rejectAuthenticationFailure(response);
  // Status 404 is the only honest "not in this authority" signal. Do not
  // parse the body into an empty ChangeDetail / StateFiles / StateDelta.
  if (response.status === 404) {
    throw new SurfaceNotFoundError(options.missing.kind, options.missing.identity);
  }
  if (!response.ok) {
    throw new SurfaceRefusalError(options.label, response.status, await response.text());
  }
  return response;
}

function parseSnapshot(raw: string): SurfaceSnapshot {
  return parseDocument(raw, isSurfaceSnapshot, 'snapshot');
}

function parseChangeDetail(raw: string): ChangeDetail {
  return parseDocument(raw, isChangeDetail, 'change');
}

function parseStateFiles(raw: string): StateFiles {
  return parseDocument(raw, isStateFiles, 'states');
}

function parseStateDelta(raw: string): StateDelta {
  return parseDocument(raw, isStateDelta, 'delta');
}

function parseDocument<T>(
  raw: string,
  guard: (value: unknown) => value is T,
  label: string,
): T {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    throw new Error(`surface ${label} is not JSON`);
  }
  if (!guard(value)) {
    throw new Error(`surface ${label} does not match the protocol`);
  }
  return value;
}

function hasRequiredKeys<Key extends string>(value: unknown, keys: readonly Key[]): value is Record<Key, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  return keys.every((key) => key in value);
}

function isStrings(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item: unknown) => typeof item === 'string');
}

function isQuantity(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

function isReplayObject(value: unknown): boolean {
  return hasRequiredKeys(value, ['digest', 'bytes', 'media_type']) && typeof value.digest === 'string' && REPLAY_DIGEST.test(value.digest)
    && isQuantity(value.bytes) && typeof value.media_type === 'string';
}

function isReplayResource(value: unknown): boolean {
  return hasRequiredKeys(value, ['name', 'quantity', 'unit', 'boundary']) && typeof value.name === 'string' && isQuantity(value.quantity)
    && typeof value.unit === 'string' && typeof value.boundary === 'string';
}

function isReplayPhase(value: unknown): boolean {
  return hasRequiredKeys(value, ['name', 'stage', 'started_offset_ns', 'wall_time_ns']) && typeof value.name === 'string' && typeof value.stage === 'string'
    && isQuantity(value.started_offset_ns) && isQuantity(value.wall_time_ns);
}

function isReplayInvocation(value: unknown): boolean {
  return hasRequiredKeys(value, ['program', 'arguments', 'exit_code', 'started_offset_ns', 'elapsed_ns', 'stdout', 'stderr', 'resources'])
    && typeof value.program === 'string' && isStrings(value.arguments)
    && (value.exit_code === null || (typeof value.exit_code === 'number' && Number.isInteger(value.exit_code)))
    && isQuantity(value.started_offset_ns) && isQuantity(value.elapsed_ns)
    && isReplayObject(value.stdout) && isReplayObject(value.stderr)
    && Array.isArray(value.resources) && value.resources.every(isReplayResource);
}

function isReplayOutcome(value: unknown): boolean {
  return value === null || (hasRequiredKeys(value, ['evaluator', 'candidate_tree', 'passed', 'invocation']) && typeof value.evaluator === 'string'
    && typeof value.candidate_tree === 'string' && typeof value.passed === 'boolean'
    && isReplayInvocation(value.invocation));
}

function isReplayFact(value: unknown): boolean {
  return hasRequiredKeys(value, ['value', 'provenance', 'sources', 'method']) && typeof value.provenance === 'string'
    && ['observed', 'reconstructed', 'inferred', 'unavailable'].includes(value.provenance)
    && isStrings(value.sources) && typeof value.method === 'string';
}

function isReplayEpisode(value: unknown): boolean {
  return hasRequiredKeys(value, ['schema', 'repository', 'pull_request', 'partition', 'smoke_only', 'captured_at',
    'reproducibility', 'reproducibility_reasons', 'facts', 'objects']) && value.schema === HISTORICAL_REPLAY_SCHEMA
    && typeof value.repository === 'string' && isQuantity(value.pull_request)
    && typeof value.partition === 'string' && typeof value.smoke_only === 'boolean'
    && typeof value.captured_at === 'string' && typeof value.reproducibility === 'string'
    && isStrings(value.reproducibility_reasons)
    && typeof value.facts === 'object' && value.facts !== null && !Array.isArray(value.facts)
    && Object.values(value.facts).every(isReplayFact)
    && typeof value.objects === 'object' && value.objects !== null && !Array.isArray(value.objects)
    && Object.values(value.objects).every(isReplayObject);
}

function isReplayRun(value: unknown, lane: 'C1' | 'M1', episode: unknown): boolean {
  if (!hasRequiredKeys(value, ['schema', 'lane', 'basis', 'candidate_commit', 'candidate_tree', 'invocations',
    'outcome', 'wall_time_ns', 'phases', 'resources', 'missing_measurements', 'authority_receipt',
    'performance_episode', 'blindness', 'raw_capture']) || value.schema !== HISTORICAL_REPLAY_SCHEMA || value.lane !== lane) return false;
  if (!hasRequiredKeys(value.basis, ['episode', 'mode', 'dimensions']) || value.basis.episode !== episode
    || (value.basis.mode !== 'exact_patch' && value.basis.mode !== 'intent')
    || typeof value.basis.dimensions !== 'object' || value.basis.dimensions === null
    || Array.isArray(value.basis.dimensions)) return false;
  return typeof value.candidate_commit === 'string' && typeof value.candidate_tree === 'string'
    && Array.isArray(value.invocations) && value.invocations.every(isReplayInvocation)
    && isReplayOutcome(value.outcome) && isQuantity(value.wall_time_ns)
    && Array.isArray(value.phases) && value.phases.every(isReplayPhase)
    && Array.isArray(value.resources) && value.resources.every(isReplayResource)
    && isStrings(value.missing_measurements)
    && (value.authority_receipt === null || isReplayObject(value.authority_receipt))
    && (value.performance_episode === null || isReplayObject(value.performance_episode))
    && (value.raw_capture === null || isReplayObject(value.raw_capture))
    && (value.blindness === null || isReplayObject(value.blindness));
}

function isReplayRatio(value: unknown): boolean {
  return value === null || (hasRequiredKeys(value, ['numerator', 'denominator']) && isQuantity(value.numerator)
    && isQuantity(value.denominator) && value.denominator > 0);
}

function isReplayComparison(value: unknown, identities: Record<string, unknown>): boolean {
  return hasRequiredKeys(value, ['schema', 'episode', 'conventional', 'matrix', 'verdict', 'reasons',
    'externally_claimable', 'matrix_substrate_speedup', 'world_workflow_compression']) && value.schema === HISTORICAL_REPLAY_SCHEMA
    && value.episode === identities.episode && value.conventional === identities.conventional
    && value.matrix === identities.matrix && typeof value.verdict === 'string'
    && isStrings(value.reasons) && typeof value.externally_claimable === 'boolean'
    && isReplayRatio(value.matrix_substrate_speedup) && isReplayRatio(value.world_workflow_compression);
}

function isReplayDetail(value: unknown): value is ReplayDetail {
  if (!hasRequiredKeys(value, ['schema', 'project', 'identities', 'episode', 'conventional', 'matrix', 'comparison'])
    || value.schema !== REPLAY_SCHEMA || typeof value.project !== 'string'
    || !hasRequiredKeys(value.identities, ['episode', 'conventional', 'matrix', 'comparison'])) return false;
  const identities = value.identities;
  if (!Object.values(identities).every((identity) =>
    typeof identity === 'string' && REPLAY_DIGEST.test(identity))) return false;
  return isReplayEpisode(value.episode)
    && isReplayRun(value.conventional, 'C1', identities.episode)
    && isReplayRun(value.matrix, 'M1', identities.episode)
    && isReplayComparison(value.comparison, identities);
}

/**
 * The website Worker holds the authority credential and attaches it
 * server-side, so a browser request carries only its session cookie. A bearer
 * is set here only when one was supplied directly — the self-hosted operator
 * path. Absence is not an error: the origin answers 401 and the shell routes
 * to sign-in, which is the same outcome without a throw that never reached the
 * network.
 */
function authorizationHeaders(
  credential: CredentialState,
  initial?: HeadersInit,
): Headers {
  const headers = new Headers(initial);
  if (credential.bearer !== undefined) {
    headers.set('authorization', `Bearer ${credential.bearer}`);
  }
  return headers;
}

/**
 * The refusal code the website Worker writes beside a 401 it raised itself.
 *
 * The `/v1/` proxy answers `{ ok: false, reason: 'no_session' }` when this
 * browser carries no session, and otherwise passes the authority's own answer
 * through untouched. So the body is what separates "sign in" from "the site's
 * credential was refused", and reading it is the only way to tell them apart.
 */
/**
 * Refusal codes the site Worker writes when this browser carries no session.
 *
 * A set rather than an equality: the enumeration is the point, and it matches
 * `LINK_REFUSALS` in `public-auth.ts`, which reads the same wire codes.
 */
const SESSION_ABSENT: ReadonlySet<string> = new Set(['no_session']);

async function proxyRefusalReason(response: Response): Promise<string> {
  try {
    const body: unknown = await response.clone().json();
    if (body && typeof body === 'object' && 'reason' in body) {
      return String((body as { reason: unknown }).reason);
    }
  } catch {
    return '';
  }
  return '';
}

/** Refusal codes the site Worker writes when the session is not a member of the project asked for. */
const PROJECT_ABSENT: ReadonlySet<string> = new Set(['no_project']);

async function rejectAuthenticationFailure(response: Response): Promise<void> {
  if (response.status !== 401 && response.status !== 403) return;
  const refusal = await proxyRefusalReason(response);
  throw new SurfaceAuthenticationError(
    SESSION_ABSENT.has(refusal) ? 'noSession' : PROJECT_ABSENT.has(refusal) ? 'noProject' : 'refused',
    response.status,
  );
}

/** Build one memory-only client scoped to a single surface credential. */
export function createSurfaceClient(): SurfaceClient {
  const credential: CredentialState = { bearer: undefined, project: null };
  return {
    selectProject: (project) => {
      credential.project = project;
    },
    project: () => credential.project,
    connect: (bearer, signal) => connect(credential, bearer, signal),
    fetchSnapshot: (signal) => fetchSnapshot(credential, signal),
    fetchActiveWork: (project, signal) => fetchActiveWork(credential, project, signal),
    fetchChangeDetail: (id, signal) => fetchChangeDetail(credential, id, signal),
    fetchReplayDetail: (digest, signal) => fetchReplayDetail(credential, digest, signal),
    fetchStateFiles: (root, signal) => fetchStateFiles(credential, root, signal),
    fetchStateFile: (root, path, signal) => fetchStateFile(credential, root, path, signal),
    fetchStateDelta: (base, result, signal) =>
      fetchStateDelta(credential, base, result, signal),
    subscribeSnapshots: (onSnapshot, onConnectionState, onClosed) =>
      subscribeSnapshots(credential, onSnapshot, onConnectionState, onClosed),
  };
}
